import { createFileRoute, Outlet, useNavigate, useRouter } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import TabLayout from "src/components/base/tab-layout";
import DEVICE_CONTEXT, { DeviceProvider } from "src/context/internal/device";
import { BackButton } from "src/components/base/button";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import RenameDeviceDialog from "src/components/dialogs/rename-device";
import ExpireDeviceKeyDialog from "src/components/dialogs/expire-device-key";
import DeleteDeviceDialog from "src/components/dialogs/delete-device";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { ChevronDownIcon, ClockIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import LinkExtraDomainDialog from "src/components/dialogs/link-extra-record";

/**
 * The properties for {@link DeviceTabs}
 */
export type DeviceTabsProps = {};

/**
 * Tabs for the device view
 */
function DeviceTabs(props: DeviceTabsProps) {
    const [t] = useTranslation("admin-device-view");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { deviceId } = Route.useParams();
    const data = Route.useLoaderData();

    const [openRenameDevice, setOpenRenameDevice] = React.useState<FullDevice>();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullDevice>();
    const [openDeleteDevice, setOpenDeleteDevice] = React.useState<FullDevice>();
    const [openAddExtraDomain, setOpenAddExtraDomain] = React.useState<string | undefined>(undefined);

    return (
        <DeviceProvider uuid={deviceId}>
            <DEVICE_CONTEXT.Consumer>
                {(deviceContext) => (
                    <div className={"flex flex-col gap-6"}>
                        <BackButton className={"self-start"} href={"/a/devices"}>
                            {t("button.back-to-device-overview")}
                        </BackButton>

                        <TabLayout
                            heading={t("heading.device-view", { device: deviceContext.device.dnsname })}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton outline={true}>
                                        <span>{tg("button.edit")}</span>
                                        <ChevronDownIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenRenameDevice(deviceContext.device)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-device")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                disabled={
                                                    data.list.length === 0 ||
                                                    (!deviceContext.device.ipv4 && !deviceContext.device.ipv6)
                                                }
                                                onClick={() => {
                                                    if (deviceContext.device.ipv4) {
                                                        setOpenAddExtraDomain(deviceContext.device.ipv4);
                                                    } else if (deviceContext.device.ipv6) {
                                                        setOpenAddExtraDomain(deviceContext.device.ipv6);
                                                    }
                                                }}
                                            >
                                                <PlusCircleIcon />
                                                <DropdownLabel>{t("button.link-extra-record")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownDivider />

                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                                            <DropdownItem onClick={() => setOpenExpireKey(deviceContext.device)}>
                                                <ClockIcon />
                                                <DropdownLabel>{t("button.expire-key")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenDeleteDevice(deviceContext.device)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-device")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/devices/$deviceId/general"} params={{ deviceId: deviceId }}>
                                        {t("heading.general")}
                                    </Tab>
                                    <Tab href={"/a/devices/$deviceId/inbound-acls"} params={{ deviceId: deviceId }}>
                                        {t("heading.inbound-acl")}
                                    </Tab>
                                    <Tab href={"/a/devices/$deviceId/outbound-acls"} params={{ deviceId: deviceId }}>
                                        {t("heading.outbound-acl")}
                                    </Tab>
                                </TabMenu>
                            }
                        >
                            <Outlet />
                        </TabLayout>

                        {openRenameDevice && (
                            <Suspense>
                                <RenameDeviceDialog
                                    device={openRenameDevice}
                                    onClose={() => setOpenRenameDevice(undefined)}
                                    onRename={() => {
                                        deviceContext.reset();
                                        setOpenRenameDevice(undefined);
                                    }}
                                />
                            </Suspense>
                        )}

                        {openExpireKey && (
                            <Suspense>
                                <ExpireDeviceKeyDialog
                                    device={openExpireKey}
                                    onClose={() => setOpenExpireKey(undefined)}
                                    onExpire={() => {
                                        deviceContext.reset();
                                        setOpenExpireKey(undefined);
                                    }}
                                />
                            </Suspense>
                        )}

                        {openDeleteDevice && (
                            <Suspense>
                                <DeleteDeviceDialog
                                    onClose={() => setOpenDeleteDevice(undefined)}
                                    onDeletion={async () => {
                                        setOpenDeleteDevice(undefined);
                                        await navigate({
                                            to: "/a/devices",
                                            search: { search: "", page: 1, filter: "all" },
                                        });
                                    }}
                                    device={openDeleteDevice}
                                />
                            </Suspense>
                        )}
                        {openAddExtraDomain && (
                            <Suspense>
                                <LinkExtraDomainDialog
                                    device_ip={openAddExtraDomain}
                                    domains={data.list}
                                    onSubmit={async () => {
                                        await router.invalidate();
                                        setOpenAddExtraDomain(undefined);
                                    }}
                                    onClose={() => setOpenAddExtraDomain(undefined)}
                                />
                            </Suspense>
                        )}
                    </div>
                )}
            </DEVICE_CONTEXT.Consumer>
        </DeviceProvider>
    );
}

export const Route = createFileRoute("/_admin/a/devices/$deviceId/_device")({
    component: DeviceTabs,
    loader: async ({ params }) => await ApiInternal.dns.getPossibleExtraDnsByDevice(params.deviceId),
});
